import axios from "axios";

window.axios = axios;

window.axios.defaults.baseURL = "https://api.members.velora.com.br/v1";
window.axios.defaults.timeout = 1000 * 60 * 5;

if (document.location.hostname.includes(".localhost")) {
  window.axios.defaults.baseURL = "http://api.members.localhost:8000/v1";

  const localHost = document.location.hostname;
  const realHost = localHost.replace(
    /(\.club)?\.localhost$/,
    ".club.looma.app.br"
  );
  window.axios.defaults.headers.common["X-Forwarded-Host"] = realHost;
}

if (document.location.href.indexOf(".staging.club") > 0) {
  window.axios.defaults.baseURL =
    "https://api-staging.members.velora.com.br/v1";
}

const ACCESS_TOKEN_KEY = "_apiToken";
const REFRESH_TOKEN_KEY = "_refreshToken";

const SKIP_REFRESH_ENDPOINTS = [
  "/accounts/signin",
  "/accounts/forgot",
  "/accounts/reset-password",
  "/users/add",
  "/accounts/refresh-token",
  "/setup/config"
];

let isRefreshing = false;
let failedQueue = [];
let logoutCallback = null;

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    error ? reject(error) : resolve(token);
  });
  failedQueue = [];
};

const setAxiosHeader = token => {
  if (token) {
    window.axios.defaults.headers.common.Authorization = `JWT ${token}`;
  } else {
    delete window.axios.defaults.headers.common.Authorization;
  }
};

const setTokens = (accessToken, refreshToken) => {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }
  setAxiosHeader(accessToken);
};

const removeTokens = () => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  setAxiosHeader(null);
};

const getToken = () => localStorage.getItem(ACCESS_TOKEN_KEY);

const getRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY);

const onLogout = callback => {
  logoutCallback = callback;
};

const shouldSkipRefresh = url => {
  return SKIP_REFRESH_ENDPOINTS.some(endpoint => url && url.includes(endpoint));
};

window.axios.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    const isUnauthorized = error.response && error.response.status === 401;
    const alreadyRetried = originalRequest._retry;
    const skipRefresh = shouldSkipRefresh(originalRequest.url);

    if (!isUnauthorized || alreadyRetried || skipRefresh) {
      return Promise.reject(error);
    }

    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      if (logoutCallback) logoutCallback();
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(token => {
        originalRequest.headers.Authorization = `JWT ${token}`;
        return window.axios(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const { data } = await window.axios.post("/accounts/refresh-token", {
        refresh: refreshToken
      });

      setTokens(data.access, data.refresh);
      processQueue(null, data.access);

      originalRequest.headers.Authorization = `JWT ${data.access}`;
      return window.axios(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      removeTokens();
      if (logoutCallback) logoutCallback();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

const init = () => {
  const token = getToken();
  if (token) {
    setAxiosHeader(token);
  }
};

export { getToken, getRefreshToken, init, setTokens, removeTokens, onLogout };
